/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useRef, useState } from 'react';
// leafletSetup 必须排在 'leaflet-draw' 之前: 它负责把全局 L 与 GoogleMutant 工厂就位
import L from '../utils/leafletSetup';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';
import { useJsApiLoader } from '@react-google-maps/api';
import { Button, Spin, Tooltip, message } from 'antd';
import {
    FullscreenExitOutlined,
    FullscreenOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    MinusOutlined,
    PlusOutlined,
} from '@ant-design/icons';
import {
    AOI_COLORS,
    selectedLevelColor,
    DEFAULT_ZOOM,
    ENTRY_POINT_COLOR,
    FIT_MAX_ZOOM,
    FIT_PADDING_PX,
    GOOGLE_MAPS_API_KEY,
    LA_CENTER,
    MIN_VERTICES,
    PARKING_POINT_COLOR,
} from '../constants';
import {
    BasemapType,
    DrawActions,
    DrawMode,
    DrawerPoint,
    GeoJsonGeometry,
    PickTarget,
    SourceVisibility,
    ViewCommand,
    Viewport,
} from '../types';
import { AoiTileLayerHandle, TileBbox, createAoiTileLayer } from '../utils/aoiTileLayer';
import BasemapSwitcher from './BasemapSwitcher';

interface Props {
    basemap: BasemapType;
    onBasemapChange: (b: BasemapType) => void;
    /** 视口加载的 AOI 面。 */
    /** 来源图层开关 (MANUAL / SURVEY / OVERTURE)。 */
    sourceVisibility: SourceVisibility;
    /** 「未生效」图层开关: 打开时瓦片请求带 includeUnverified=true, 待审核面以虚线画出; 默认关 = 与从前一致。 */
    showUnverified: boolean;
    /** 数据版本号: 写操作后自增, 让瓦片 URL 变化, 绕过浏览器缓存重拉。 */
    tileVersion: number;
    /** 本次版本推进对应的编辑范围; 有值则按范围失效 (范围外瓦片继续吃浏览器缓存), null 走全局失效兜底。 */
    tileInvalidationBbox: TileBbox | null;
    selectedAoiId: number | null;
    /** 选中 AOI 的权威几何 (detail.selfGeometry), 保证不在视口缓存里也能高亮。 */
    selectedGeometry: GeoJsonGeometry | null;
    /** 选中 AOI 的级别 (LEVEL_1 / LEVEL_2); 选中高亮 = 级别色加深, detail 未回来时为 null 走兜底色。 */
    selectedLevelEnum: string | null;
    /** 「飞到坐标」的落点标记 (纯视觉参照, 不可点击, 不参与业务); null = 不显示。 */
    coordMarker: { lat: number; lng: number } | null;
    /** 全屏 (沉浸) 模式开关状态。 */
    fullscreen: boolean;
    onToggleFullscreen: () => void;
    /** 全屏下是否显示筛选区/列表 (仅全屏时该按钮才出现)。 */
    fullscreenChrome: boolean;
    onToggleFullscreenChrome: () => void;
    /**
     * 编辑形状中 / 有待保存的形状改动时为 true: 把瓦片里选中面的旧几何隐藏掉,
     * 只显示编辑层/高亮层的新形状 —— 否则新旧两个形状叠影。
     */
    suppressSelectedInTiles: boolean;
    /** 保存冲突时后端返回的冲突面 - 标红。 */
    conflictIds: number[];
    drawMode: DrawMode;
    /** edit 模式下正在编辑的几何 (受控: 页面传 edited ?? 原几何)。 */
    editGeometry: GeoJsonGeometry | null;
    /** 新画完待创建的几何 (蓝色渲染)。 */
    draftGeometry: GeoJsonGeometry | null;
    pickTarget: PickTarget;
    /** 抽屉当前的进门 / 停车点 (地图上打点显示)。 */
    entryPoints: DrawerPoint[];
    parkingPoints: DrawerPoint[];
    /** 一次性视图命令 (fit / set), nonce 变化才执行。 */
    viewCommand: ViewCommand | null;
    onViewportChange: (vp: Viewport) => void;
    onSelectAoi: (aoiId: number, zipcode: string | null) => void;
    onPickPoint: (lng: number, lat: number) => void;
    onDrawComplete: (geometry: GeoJsonGeometry) => void;
    onEditChange: (geometry: GeoJsonGeometry) => void;
    /** 绘制中的命令式操作出口 (撤销上一点 / 闭合); 绘制结束自动置空。 */
    drawActionsRef?: React.MutableRefObject<DrawActions | null>;
    /** 绘制中已点顶点数变化 (工具栏据此决定"撤销/闭合"能不能点)。 */
    onDrawVertexCountChange?: (count: number) => void;
    /**
     * 地图右侧浮层 (底图切换 / 缩放按钮) 距右边缘的距离 (px)。
     * 属性抽屉是绝对定位盖在地图右侧的, 抽屉打开时这两个浮层要整体左移, 否则被压在抽屉底下看不见。
     */
    overlayRightOffset?: number;
    /** 工具栏 / 图例 / 提示条等覆盖物 (绝对定位在地图上)。 */
    children?: React.ReactNode;
}

/** 取多边形外环 (Polygon → coordinates[0]; MultiPolygon → 第一个 polygon 的外环)。 */
function outerRing(geometry: GeoJsonGeometry): number[][] {
    if (geometry.type === 'Polygon') {
        return ((geometry.coordinates as number[][][])[0]) ?? [];
    }
    if (geometry.type === 'MultiPolygon') {
        return ((geometry.coordinates as number[][][][])[0]?.[0]) ?? [];
    }
    return [];
}

/** GeoJSON 环 ([lng,lat]) → Leaflet LatLng ([lat,lng]), 去掉闭合重复点。 */
function ringToLatLngs(ring: number[][]): L.LatLngTuple[] {
    const open = ring.length >= 2
        && ring[0][0] === ring[ring.length - 1][0]
        && ring[0][1] === ring[ring.length - 1][1]
        ? ring.slice(0, -1)
        : ring;
    return open.map(([lng, lat]) => [lat, lng] as L.LatLngTuple);
}

/** Leaflet LatLng 数组 → 闭合的 GeoJSON Polygon。 */
function latLngsToPolygon(latlngs: L.LatLng[]): GeoJsonGeometry {
    const coords = latlngs.map((ll) => [ll.lng, ll.lat]);
    coords.push([...coords[0]]);
    return { type: 'Polygon', coordinates: [coords] };
}

/**
 * 地图画布 —— Leaflet 单引擎 (与产品原型同栈: Leaflet 1.9.4 + leaflet-draw 1.0.4)。
 *
 * <p><b>为什么收敛到 Leaflet</b>: 原型的绘制/编辑手感来自 leaflet-draw (橡皮筋跟随、
 * 顶点 + 中点手柄、自相交拦截), 那是 Leaflet 生态的插件。此前分别用 deck.gl
 * editable-layers / mapbox-gl-draw / Google DrawingManager 各写一套, 既出现"某底图能画
 * 某底图不能"的问题, 手感也三套各不相同。收敛后: 一套绘制、一套渲染、三个底图,
 * 且与产品验收基准完全一致。
 *
 * <p><b>三底图</b>: OSM 瓦片 / Esri World Imagery 卫星 (这两个与原型同源) /
 * Google 路网 (GoogleMutant —— 走 Google 官方 JS API + 项目 key 把画面同步成 Leaflet 图层,
 * 不是直接取瓦片, 合规)。
 *
 * <p><b>面渲染</b>: 走矢量瓦片 (VectorGrid 自带 per-tile SVG renderer), 主地图 renderer 只画
 * 选中高亮 / 草稿 / 进门点等个位数覆盖物。
 */
const MapCanvas: React.FC<Props> = (props) => {
    const { basemap, drawMode, viewCommand, children } = props;

    /** 最新 props / 回调收进 ref, 命令式 Leaflet handler 不吃 stale closure。 */
    const stateRef = useRef(props);
    stateRef.current = props;

    const containerRef = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<L.Map | null>(null);
    const [mapReady, setMapReady] = useState(false);

    const basesRef = useRef<Record<BasemapType, L.Layer | null>>({ osm: null, satellite: null, google: null });
    const overlayLayerRef = useRef<L.LayerGroup | null>(null);
    const pointLayerRef = useRef<L.LayerGroup | null>(null);
    /** 「飞到坐标」落点标记的独立图层: 与进门点/停车点分开, 两边各自 clearLayers 不互相清掉。 */
    const coordMarkerLayerRef = useRef<L.LayerGroup | null>(null);
    const editPolyRef = useRef<any>(null);
    const tileHandleRef = useRef<AoiTileLayerHandle | null>(null);

    // GoogleMutant 需要 google.maps 全局就位。
    // 注意: useJsApiLoader 是全局单例, 所有页面的选项必须完全一致 —— 项目里其他调用点
    // 都不传 libraries (吃默认值 ['maps']), 这里也必须保持不传, 否则谁后加载谁白屏
    // (2026-08-05 事故: 曾传 ['drawing'] 与 5 个吃默认值的旧页面互斥)。
    const { isLoaded: googleLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    });

    /* ====================== 初始化 ====================== */

    useEffect(() => {
        if (mapRef.current || !containerRef.current) {
            return undefined;
        }
        const container = containerRef.current;
        const map = L.map(container, {
            center: [LA_CENTER.lat, LA_CENTER.lng],
            zoom: DEFAULT_ZOOM,
            zoomControl: false,
            attributionControl: false,
            // 千万不要开 preferCanvas: 它会给选中高亮/点位创建一张全图 canvas 盖在 overlayPane,
            // elementFromPoint 全命中 canvas, 下层瓦片 SVG 的点击整体失效 ——
            // 表现为"从列表选中过一次之后, 地图上的轮廓就再也点不动了"。
            // 旧 bbox 方案一屏 2000 个面才需要 canvas; 现在面在 VectorGrid 自己的 renderer 里,
            // 主地图 renderer 只画高亮/草稿/进门点 (个位数), SVG 绰绰有余,
            // 且 SVG 的 interactive:false path 是 pointer-events:none, 点击可穿透。
        });
        mapRef.current = map;

        // OSM 直连 CDN 立刻出图, 作为 Google JS API 异步就位前的兜底底图先挂上;
        // Google 路网/卫星层在下面的切换 effect 里等 googleLoaded 后懒建并顶替
        basesRef.current.osm = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19 });
        basesRef.current.osm.addTo(map);

        overlayLayerRef.current = L.layerGroup().addTo(map);
        pointLayerRef.current = L.layerGroup().addTo(map);
        coordMarkerLayerRef.current = L.layerGroup().addTo(map);

        const report = () => {
            const b = map.getBounds();
            stateRef.current.onViewportChange({
                minLng: b.getWest(),
                minLat: b.getSouth(),
                maxLng: b.getEast(),
                maxLat: b.getNorth(),
                zoom: map.getZoom(),
            });
        };
        map.on('moveend', report);
        map.on('zoomend', report);
        map.on('click', (e: L.LeafletMouseEvent) => {
            const st = stateRef.current;
            if (st.pickTarget && st.drawMode === 'idle') {
                st.onPickPoint(e.latlng.lng, e.latlng.lat);
            }
        });

        // 容器尺寸变化 (列表收起 / 抽屉滑出) 要让 Leaflet 重算
        const ro = new ResizeObserver(() => map.invalidateSize());
        ro.observe(container);

        setMapReady(true);
        report();

        return () => {
            ro.disconnect();
            map.remove();
            mapRef.current = null;
            setMapReady(false);
        };
    }, []);

    /* ====================== 底图切换 ====================== */

    useEffect(() => {
        const map = mapRef.current;
        if (!map || !mapReady) {
            return;
        }
        // Google 路网 / 卫星图层懒建 (等官方 JS API 就位; 就位前维持 OSM 兜底)
        if (basemap === 'google' && !basesRef.current.google) {
            if (!googleLoaded) {
                return;
            }
            basesRef.current.google = (L.gridLayer as any).googleMutant({ type: 'roadmap', maxZoom: 21 });
        }
        if (basemap === 'satellite' && !basesRef.current.satellite) {
            if (!googleLoaded) {
                return;
            }
            // hybrid = 卫星影像 + 路名标注, 对齐 Google Maps 自己的"卫星"视图 (纯 satellite 没有路名, 没法定位)
            basesRef.current.satellite = (L.gridLayer as any).googleMutant({ type: 'hybrid', maxZoom: 21 });
        }
        (Object.keys(basesRef.current) as BasemapType[]).forEach((key) => {
            const layer = basesRef.current[key];
            if (!layer) {
                return;
            }
            if (key === basemap) {
                if (!map.hasLayer(layer)) {
                    layer.addTo(map);
                }
            } else if (map.hasLayer(layer)) {
                map.removeLayer(layer);
            }
        });
    }, [basemap, mapReady, googleLoaded]);

    /* ====================== AOI 面渲染 (矢量瓦片) ====================== */

    // 瓦片图层只建一次: 选中 / 冲突 / 图层开关 / 版本变化都通过 handle 增量更新,
    // 重建图层会让已加载的瓦片全部作废重拉。
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !mapReady) {
            return undefined;
        }
        const handle = createAoiTileLayer({
            version: stateRef.current.tileVersion,
            sourceVisibility: stateRef.current.sourceVisibility,
            includeUnverified: stateRef.current.showUnverified,
            onSelect: (aoiId, zipcode) => stateRef.current.onSelectAoi(aoiId, zipcode),
            isPickingOrDrawing: () => {
                const st = stateRef.current;
                return st.drawMode !== 'idle' || st.pickTarget != null;
            },
        });
        handle.layer.addTo(map);
        tileHandleRef.current = handle;
        return () => {
            tileHandleRef.current = null;
            handle.destroy();
        };
    }, [mapReady]);

    useEffect(() => {
        tileHandleRef.current?.setSelected(props.selectedAoiId, props.selectedLevelEnum);
    }, [props.selectedAoiId, props.selectedLevelEnum]);

    useEffect(() => {
        tileHandleRef.current?.setHidden(props.suppressSelectedInTiles ? props.selectedAoiId : null);
    }, [props.suppressSelectedInTiles, props.selectedAoiId]);

    useEffect(() => {
        tileHandleRef.current?.setConflicts(props.conflictIds);
    }, [props.conflictIds]);

    useEffect(() => {
        tileHandleRef.current?.setSourceVisibility(props.sourceVisibility);
    }, [props.sourceVisibility]);

    useEffect(() => {
        tileHandleRef.current?.setIncludeUnverified(props.showUnverified);
    }, [props.showUnverified]);

    useEffect(() => {
        tileHandleRef.current?.setVersion(props.tileVersion, props.tileInvalidationBbox);
        // bbox 与 version 在页面侧同一个 state 里原子更新, 只跟 version 触发即可
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.tileVersion]);

    /* ====================== 选中 / 草稿几何高亮 ====================== */

    useEffect(() => {
        const group = overlayLayerRef.current;
        if (!group || !mapReady) {
            return;
        }
        group.clearLayers();
        // 编辑态由 editPoly 负责渲染, 这里不重复画
        const geo = props.drawMode === 'edit' ? null : (props.draftGeometry ?? props.selectedGeometry);
        if (!geo) {
            return;
        }
        const ring = outerRing(geo);
        if (ring.length < 3) {
            return;
        }
        // 草稿(刚画完未保存)保持工作态蓝; 已存在面的选中高亮用级别加深色 (1级深青/2级深紫)
        const color = props.draftGeometry
            ? AOI_COLORS.SELECTED.hex
            : selectedLevelColor(props.selectedLevelEnum).hex;
        L.polygon(ringToLatLngs(ring), {
            color,
            weight: 3,
            fillColor: color,
            fillOpacity: 0.22,
            interactive: false,
        }).addTo(group);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.selectedGeometry, props.draftGeometry, props.drawMode, props.selectedLevelEnum, mapReady]);

    /* ====================== 全屏切换后重算地图尺寸 ====================== */

    // 容器尺寸由 CSS 切换 (fixed/inset:0), Leaflet 不会自己发现 —— 必须 invalidateSize,
    // 否则瓦片按旧尺寸铺、点击坐标也会偏。初始化时挂的 ResizeObserver 会兜住, 这里再显式调一次,
    // 保证同一帧内就正确 (ResizeObserver 回调可能晚一帧, 用户会看到一次跳动)。
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !mapReady) {
            return;
        }
        map.invalidateSize();
    }, [props.fullscreen, props.fullscreenChrome, mapReady]);

    /* ====================== 「飞到坐标」落点标记 ====================== */

    useEffect(() => {
        const group = coordMarkerLayerRef.current;
        if (!group || !mapReady) {
            return;
        }
        group.clearLayers();
        const c = props.coordMarker;
        if (!c) {
            return;
        }
        // 十字准星 (divIcon, interactive:false 不吃点击, 不挡下面的瓦片轮廓)
        L.marker([c.lat, c.lng], {
            interactive: false,
            icon: L.divIcon({
                className: '',
                html: `<div style="position:relative;width:22px;height:22px">
                    <div style="position:absolute;left:10px;top:0;width:2px;height:22px;background:${AOI_COLORS.CONFLICT.hex}"></div>
                    <div style="position:absolute;top:10px;left:0;height:2px;width:22px;background:${AOI_COLORS.CONFLICT.hex}"></div>
                </div>`,
                iconSize: [22, 22],
                iconAnchor: [11, 11],
            }),
        }).addTo(group);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.coordMarker, mapReady]);

    /* ====================== 进门 / 停车点 ====================== */

    useEffect(() => {
        const group = pointLayerRef.current;
        if (!group || !mapReady) {
            return;
        }
        group.clearLayers();
        const mark = (pts: DrawerPoint[], color: string, prefix: string) => {
            pts.forEach((p, i) => {
                if (p.lng == null || p.lat == null) {
                    return;
                }
                L.marker([p.lat, p.lng], {
                    interactive: false,
                    // 倒水滴 pin: 圆角方块留左下尖角, 必须 rotate(-45deg) 尖角才转到正下方
                    // (demo 原样式写的 +45, 尖角其实指向左侧, 图标看起来飘在点位上方不指向任何地方)。
                    // 锚点精确压在尖角上: 尖角距中心 14*sqrt(2)≈20px → 图标坐标 (14, 34), 点哪尖角戳哪。
                    icon: L.divIcon({
                        className: '',
                        html: `<div style="min-width:28px;height:28px;padding:0 5px;border-radius:50% 50% 50% 2px;
                            transform:rotate(-45deg);background:${color};box-shadow:0 2px 6px rgba(0,0,0,.35);
                            display:flex;align-items:center;justify-content:center">
                            <span style="transform:rotate(45deg);color:#fff;font-size:11px;font-weight:700;
                                white-space:nowrap">${prefix}${i + 1}</span>
                        </div>`,
                        iconSize: [28, 28],
                        iconAnchor: [14, 34],
                    }),
                }).addTo(group);
            });
        };
        mark(props.entryPoints, ENTRY_POINT_COLOR.hex, '门');
        mark(props.parkingPoints, PARKING_POINT_COLOR.hex, 'P');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.entryPoints, props.parkingPoints, mapReady]);

    /* ====================== 绘制 (leaflet-draw, 与原型同一控件) ====================== */

    useEffect(() => {
        const map = mapRef.current;
        if (!map || !mapReady || drawMode !== 'draw') {
            return undefined;
        }
        const handler = new (L as any).Draw.Polygon(map, {
            allowIntersection: false, // 自相交当场拦截, 不用等保存被后端拒
            showArea: false,
            guidelineDistance: 10,
            shapeOptions: {
                color: AOI_COLORS.SELECTED.hex,
                weight: 2.5,
                fillColor: AOI_COLORS.SELECTED.hex,
                fillOpacity: 0.18,
            },
            drawError: { color: AOI_COLORS.CONFLICT.hex, message: '边不能自相交' },
        });

        // 关掉"双击最后一个顶点闭合" (2026-08-05 需求: 太容易误触, 画到一半就提前闭合)。
        // leaflet-draw 的 _updateFinishHandler 干两件事: 给第 1 个顶点绑 click (点回起点闭合),
        // 给最后一个顶点绑 dblclick。这里只保留前者 —— 收尾走工具栏的「完成绘制」或点回起点,
        // 都是明确的单次动作。覆写实例方法而不是改库, 不影响其他用到 leaflet-draw 的页面。
        handler._updateFinishHandler = function updateFinishHandlerNoDblClick(this: any) {
            if (this._markers.length === 1) {
                this._markers[0].on('click', this._finishShape, this);
            }
        };

        // 双击不再闭合后, 必须同时禁掉地图的双击缩放 —— 否则画到一半双击会突然缩放,
        // 比原来的误闭合更碍事 (leaflet-draw 自己不管这个)。绘制结束在 cleanup 里恢复。
        map.doubleClickZoom.disable();
        handler.enable();
        // leaflet-draw 没有"顶点数"的公开读法, 但每加一个点都会抛 DRAWVERTEX (带全部顶点图层);
        // 撤销是命令式的、不抛事件, 所以撤销后从 handler 自己的 _markers 补一次数。
        const reportCount = (count: number) => stateRef.current.onDrawVertexCountChange?.(count);
        reportCount(0);
        const onVertex = (e: any) => reportCount(e.layers?.getLayers?.().length ?? 0);
        map.on((L as any).Draw.Event.DRAWVERTEX, onVertex);
        const actionsRef = props.drawActionsRef;
        if (actionsRef) {
            actionsRef.current = {
                deleteLastVertex: () => {
                    try {
                        handler.deleteLastVertex();
                    } catch {
                        /* 还没点过顶点, 忽略 */
                    }
                    reportCount(handler._markers?.length ?? 0);
                },
                completeShape: () => {
                    // 顶点不足时 completeShape 是静默 no-op, 会让用户以为按钮坏了, 这里先自己挡
                    if ((handler._markers?.length ?? 0) < MIN_VERTICES) {
                        message.warning({ content: `至少需要 ${MIN_VERTICES} 个顶点`, key: 'vertex-limit' });
                        return;
                    }
                    try {
                        handler.completeShape();
                    } catch {
                        message.warning({ content: '闭合失败, 请检查顶点是否自相交', key: 'vertex-limit' });
                    }
                },
            };
        }

        const onCreated = (e: any) => {
            const latlngs: L.LatLng[] = e.layer?.getLatLngs?.()?.[0] ?? [];
            if (latlngs.length < MIN_VERTICES) {
                message.warning({ content: `至少需要 ${MIN_VERTICES} 个顶点`, key: 'vertex-limit' });
                return;
            }
            // 顶点上限不在前端卡 (后端动态配置), 超了让后端 VERTEX_LIMIT 带着真实范围打回
            stateRef.current.onDrawComplete(latLngsToPolygon(latlngs));
        };
        map.on((L as any).Draw.Event.CREATED, onCreated);

        return () => {
            map.off((L as any).Draw.Event.CREATED, onCreated);
            map.off((L as any).Draw.Event.DRAWVERTEX, onVertex);
            reportCount(0);
            if (actionsRef) {
                actionsRef.current = null;
            }
            try {
                handler.disable();
                // 恢复双击缩放 (绘制期间为免误触被禁掉); 不恢复的话退出绘制后地图永远双击不了
                map.doubleClickZoom.enable();
            } catch {
                /* 地图已销毁 */
            }
        };
    }, [drawMode, mapReady]);

    /* ====================== 编辑形状 (leaflet-draw editing: 顶点 + 中点手柄) ====================== */

    useEffect(() => {
        const map = mapRef.current;
        if (!map || !mapReady || drawMode !== 'edit') {
            return undefined;
        }
        const geometry = stateRef.current.editGeometry;
        if (!geometry) {
            return undefined;
        }
        const ring = outerRing(geometry);
        if (ring.length < 3) {
            return undefined;
        }
        const poly: any = L.polygon(ringToLatLngs(ring), {
            color: AOI_COLORS.SELECTED.hex,
            weight: 3,
            fillColor: AOI_COLORS.SELECTED.hex,
            fillOpacity: 0.2,
        }).addTo(map);
        editPolyRef.current = poly;
        poly.editing.enable();

        const emit = () => {
            const latlngs: L.LatLng[] = poly.getLatLngs()?.[0] ?? [];
            if (latlngs.length < MIN_VERTICES) {
                return;
            }
            stateRef.current.onEditChange(latLngsToPolygon(latlngs));
        };
        // 拖顶点 / 拖中点插点 / 删顶点都会触发 edit
        poly.on('edit', emit);

        return () => {
            poly.off('edit', emit);
            try {
                poly.editing.disable();
                map.removeLayer(poly);
            } catch {
                /* 地图已销毁 */
            }
            editPolyRef.current = null;
        };
        // editGeometry 每次拖动都变, 有意不进 deps (否则拖一下重挂一次)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drawMode, mapReady]);

    /* ====================== 光标 ====================== */

    useEffect(() => {
        const map = mapRef.current;
        if (!map || !mapReady) {
            return;
        }
        map.getContainer().style.cursor = props.pickTarget ? 'crosshair' : '';
    }, [props.pickTarget, mapReady]);

    /* ====================== 视图命令 (fit / set) ====================== */

    useEffect(() => {
        const map = mapRef.current;
        if (!map || !mapReady || !viewCommand) {
            return;
        }
        if (viewCommand.kind === 'fit') {
            map.fitBounds(
                L.latLngBounds(
                    [viewCommand.minLat, viewCommand.minLng],
                    [viewCommand.maxLat, viewCommand.maxLng],
                ),
                { padding: [FIT_PADDING_PX, FIT_PADDING_PX], maxZoom: viewCommand.maxZoom ?? FIT_MAX_ZOOM },
            );
            // 大面 fit 完可能落在 MIN_RENDER_ZOOM 以下, 那样后端一个面都不返回, 周边全空白
            if (viewCommand.minZoom != null && map.getZoom() < viewCommand.minZoom) {
                map.setZoom(viewCommand.minZoom);
            }
        } else {
            map.setView([viewCommand.lat, viewCommand.lng], viewCommand.zoom);
        }
    }, [viewCommand, mapReady]);

    /* ====================== 缩放按钮 ====================== */

    const zoomBy = useCallback((delta: number) => {
        const map = mapRef.current;
        if (map) {
            map.setZoom(map.getZoom() + delta);
        }
    }, []);

    return (
        <div className="absolute bottom-0 left-0 right-0 top-0 overflow-hidden" style={{ background: '#f0f2f5' }}>
            <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
            {!mapReady && (
                <div className="absolute inset-0 flex items-center justify-center">
                    <Spin />
                </div>
            )}

            {/* 右下角缩放 */}
            <div
                className="absolute flex flex-col overflow-hidden rounded-lg bg-white shadow-md"
                style={{ bottom: 16, right: props.overlayRightOffset ?? 12, zIndex: 1200 }}
            >
                <Button
                    type="text"
                    icon={<PlusOutlined />}
                    onClick={() => zoomBy(1)}
                    style={{ width: 34, height: 34, borderRadius: 0, borderBottom: '1px solid #f0f0f0' }}
                />
                <Button
                    type="text"
                    icon={<MinusOutlined />}
                    onClick={() => zoomBy(-1)}
                    style={{ width: 34, height: 34, borderRadius: 0 }}
                />
            </div>

            {/* 全屏 / 全屏内显示筛选列表 (右下角, 缩放按钮上方) */}
            <div
                className="absolute flex flex-col overflow-hidden rounded-lg bg-white shadow-md"
                style={{ bottom: 90, right: props.overlayRightOffset ?? 12, zIndex: 1200 }}
            >
                <Tooltip title={props.fullscreen ? '退出全屏 (Esc)' : '全屏显示地图'} placement="left">
                    <Button
                        type="text"
                        icon={props.fullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                        onClick={props.onToggleFullscreen}
                        style={{ width: 34, height: 34, borderRadius: 0 }}
                    />
                </Tooltip>
                {props.fullscreen && (
                    <Tooltip title={props.fullscreenChrome ? '隐藏筛选区与列表' : '显示筛选区与列表'} placement="left">
                        <Button
                            type="text"
                            icon={props.fullscreenChrome ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
                            onClick={props.onToggleFullscreenChrome}
                            style={{ width: 34, height: 34, borderRadius: 0, borderTop: '1px solid #f0f0f0' }}
                        />
                    </Tooltip>
                )}
            </div>

            {/* 底图三选 (右上角) */}
            <BasemapSwitcher
                value={basemap}
                onChange={props.onBasemapChange}
                rightOffset={props.overlayRightOffset ?? 12}
            />

            {children}
        </div>
    );
};

export default MapCanvas;
